import { useSessionValue } from '@portal/appLayer/auth';
import { environment } from '@portal/constants/environment';
import { createClientStatsigUser } from '@portal/shared/utils/statsig';
import { useStoreValue } from '@portal/state/hooks/useStore';
import {
  StatsigProvider as BaseStatsigProvider,
  LogLevel,
  StatsigUser,
  useStatsigClient
} from '@statsig/react-bindings';
import { StatsigSessionReplayPlugin } from '@statsig/session-replay';
import { StatsigAutoCapturePlugin } from '@statsig/web-analytics';
import { useRouter } from 'next/router';
import { ReactNode, useEffect, useMemo } from 'react';

const StatsigUserUpdater = ({ user }: { user: StatsigUser }) => {
  const { client } = useStatsigClient();

  useEffect(() => {
    const newUser = structuredClone(user);
    client.updateUserAsync(newUser);
  }, [client, user]);

  return null;
};

export const StatsigProvider = ({ children }: { children: ReactNode }) => {
  const router = useRouter();
  const store = useStoreValue();
  const session = useSessionValue();

  const path = router.asPath;

  const user = useMemo(
    () => createClientStatsigUser(session?.customer, store, { path }),
    [session, store, path]
  );

  return (
    <BaseStatsigProvider
      sdkKey={process.env.NEXT_PUBLIC_STATSIG_CLIENT_API_KEY!}
      user={user}
      options={{
        logLevel: process.env.NODE_ENV === 'development' ? LogLevel.Debug : LogLevel.Error,
        plugins: [new StatsigAutoCapturePlugin(), new StatsigSessionReplayPlugin()],
        environment: {
          tier: environment
        }
      }}>
      <StatsigUserUpdater user={user} />
      {children}
    </BaseStatsigProvider>
  );
};
